import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import useAuthStore from '../../store/authStore';
import { toAssetUrl } from '../../utils/assetBase';
import { isFreePlan } from '../../utils/planAccess';
import { fmtDate, fmtTime, fmtDateTime } from '../../utils/dateFormat';
import { printBill, getReceiptPrinterName } from '../../utils/printService';

const WEBVIEW_PRINT_TIMEOUT_MS = 15000;

const toMoney = (value) => Number(value || 0);
const formatMoney = (value) => `${toMoney(value).toFixed(2)} ₼`;
const trimQty = (qty, digits = 3) => Number(qty || 0).toFixed(digits).replace(/\.?0+$/, '');

const formatQty = (qty, saleType) => {
    if ((saleType || 'piece') === 'piece') return `${Math.round(Number(qty || 0))}əd`;
    if (saleType === 'weight') return `${trimQty(qty)}kq`;
    if (saleType === 'liter') return `${trimQty(qty)}lt`;
    return trimQty(qty);
};

// Set komponentlərini qruplaşdır — eyni set_group_id-li sətirləri tək sətr kimi göstər
const groupSetItems = (items = []) => {
    const groups = new Map();
    const list = [];
    for (const it of items) {
        if (it.set_group_id) {
            let g = groups.get(it.set_group_id);
            if (!g) {
                g = {
                    _isSetGroup: true,
                    id: `setgroup_${it.set_group_id}`,
                    item_name: it.note || 'Set',
                    note: '',
                    sale_type: 'piece',
                    _total: 0,
                    _minCount: Infinity
                };
                groups.set(it.set_group_id, g);
                list.push(g);
            }
            g._total += Number(it.qty) * Number(it.unit_price);
            const unit = Number(it.set_unit_qty || 0);
            const cnt = unit > 0 ? Number(it.qty) / unit : 1;
            if (cnt < g._minCount) g._minCount = cnt;
        } else {
            list.push(it);
        }
    }
    for (const g of list) {
        if (g._isSetGroup) {
            g.qty = Math.max(1, Math.round(g._minCount || 1));
            g.unit_price = g.qty > 0 ? g._total / g.qty : 0;
        }
    }
    return list;
};

const getReceiptTitle = (currentUser) => (
    currentUser?.tenant_plan === 'pro'
        ? (currentUser?.tenant_name || 'Fiscal POS')
        : 'Fiscal POS'
);

const DEFAULT_RECEIPT_LOGO = `${window.location.origin}/FiscalPOS_black.png`;

const getReceiptLogoUrl = (currentUser) => {
    if (isFreePlan(currentUser?.tenant_plan)) return null;
    if (currentUser?.receipt_logo_url) return toAssetUrl(currentUser.receipt_logo_url);
    return DEFAULT_RECEIPT_LOGO;
};

const buildReceiptText = (currentOrder, currentUser) => {
    const printedAt = fmtDateTime(new Date());

    const subtotal = toMoney(currentOrder?.subtotal);
    const servicePercent = toMoney(currentOrder?.service_percent);
    const serviceAmount = toMoney(currentOrder?.service_amount);
    const discountAmount = toMoney(currentOrder?.discount_amount);
    const appliedDeposit = toMoney(currentOrder?.applied_deposit);
    const grandTotal = toMoney(currentOrder?.grand_total || subtotal + serviceAmount - discountAmount + appliedDeposit);
    const payment = currentOrder?.payment;
    const cashAmount = toMoney(payment?.cash_amount);
    const cardAmount = toMoney(payment?.card_amount);

    const lines = [
        getReceiptTitle(currentUser),
        'Musteri ceki',
        '-------------------------------',
        `Telefon: ${currentOrder?.tenant_phone || '-'}`,
        `Masa: ${currentOrder?.table_name || '-'}`,
        `Sifaris: #${currentOrder?.id || '-'}`,
        `Ofisiant: ${currentOrder?.waiter_name || '-'}`,
        `Tarix: ${printedAt}`,
        '-------------------------------'
    ];

    groupSetItems(currentOrder?.items || []).forEach((item) => {
        const itemTotal = parseFloat(item.unit_price) * Number(item.qty);
        const qtyLabel = item._isSetGroup ? `${item.qty}ed` : formatQty(item.qty, item.sale_type);
        lines.push(item._isSetGroup ? `[SET] ${item.item_name}` : item.item_name);
        lines.push(`  ${qtyLabel} x ${parseFloat(item.unit_price).toFixed(2)} = ${itemTotal.toFixed(2)} AZN`);
        if (!item._isSetGroup && item.note) lines.push(`  Qeyd: ${item.note}`);
    });

    lines.push('-------------------------------');
    lines.push(`Cem: ${subtotal.toFixed(2)} AZN`);
    lines.push(`${trimQty(servicePercent, 2)}% Servis Haqqi: ${serviceAmount.toFixed(2)} AZN`);
    lines.push(`Depozit: ${appliedDeposit.toFixed(2)} AZN`);
    lines.push(`Endirim: ${discountAmount.toFixed(2)} AZN`);
    lines.push(`Umumi cem: ${grandTotal.toFixed(2)} AZN`);

    if (payment) {
        lines.push('-------------------------------');
        if (payment.method === 'hesab') {
            lines.push(`Nagd: ${cashAmount.toFixed(2)} AZN`);
            lines.push(`Kart: ${cardAmount.toFixed(2)} AZN`);
        } else {
            lines.push(`Odenis novü: ${payment.method === 'kart' ? 'Kart' : payment.method === 'delivery' ? 'Food delivery' : 'Nagd'}`);
            lines.push(`Odenilib: ${toMoney(payment.amount).toFixed(2)} AZN`);
        }
    }

    if (currentOrder?.note) {
        lines.push('-------------------------------');
        lines.push(`Umumi qeyd: ${currentOrder.note}`);
    }

    lines.push('');
    lines.push('Tesekkur edirik!');
    lines.push('Powered by www.fiscalbusiness.net');

    return lines.join('\n');
};

const hasWebViewBridge = () => Boolean(window.chrome?.webview?.postMessage);

const sendWebViewPrintRequest = (message) => new Promise((resolve, reject) => {
    if (!hasWebViewBridge()) {
        reject(new Error('Bu funksiya yalnız Windows tətbiqində işləyir.'));
        return;
    }

    const requestId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const timeoutId = window.setTimeout(() => {
        window.chrome.webview.removeEventListener('message', handleMessage);
        reject(new Error('Çap cavabı gözlənilən vaxtda gəlmədi.'));
    }, WEBVIEW_PRINT_TIMEOUT_MS);

    const handleMessage = (event) => {
        const payload = event?.data;
        if (!payload || payload.type !== 'print-bill-result' || payload.requestId !== requestId) return;

        window.clearTimeout(timeoutId);
        window.chrome.webview.removeEventListener('message', handleMessage);

        if (payload.ok) {
            resolve(payload);
            return;
        }

        reject(new Error(payload.error || 'Çap baş tutmadı.'));
    };

    window.chrome.webview.addEventListener('message', handleMessage);
    window.chrome.webview.postMessage({ ...message, requestId });
});

const WaiterReceipt = () => {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuthStore();

    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [printing, setPrinting] = useState(false);

    useEffect(() => {
        loadOrder();
    }, [orderId]);

    const loadOrder = async () => {
        try {
            const res = await api.get(`/orders/${orderId}`);
            setOrder(res.data.data);
        } catch {
            toast.error('Qəbz tapılmadı.');
            navigate('/waiter/tables');
        } finally {
            setLoading(false);
        }
    };

    const subtotal = toMoney(order?.subtotal);
    const displayItems = useMemo(() => groupSetItems(order?.items || []), [order?.items]);
    const servicePercent = toMoney(order?.service_percent);
    const serviceAmount = toMoney(order?.service_amount);
    const discountAmount = toMoney(order?.discount_amount);
    const appliedDeposit = toMoney(order?.applied_deposit);
    const grandTotal = toMoney(order?.grand_total || subtotal + serviceAmount - discountAmount + appliedDeposit);
    const payment = order?.payment || null;
    const paid = toMoney(payment?.amount || grandTotal);
    const cashAmount = toMoney(payment?.cash_amount);
    const cardAmount = toMoney(payment?.card_amount);
    const change = payment?.method === 'nagd' ? paid - grandTotal : 0;

    const methodNames = { nagd: 'Nağd', kart: 'Kart', hesab: 'Hesab bölündü', delivery: 'Food delivery' };
    const methodIcons = { nagd: '💵', kart: '💳', hesab: '🧾', delivery: '🛵' };

    const handlePrint = async () => {
        if (!order?.items?.length) {
            toast.error('Sifarişdə məhsul yoxdur.');
            return;
        }

        setPrinting(true);
        try {
            const receiptPrinter = await getReceiptPrinterName();
            await printBill(receiptPrinter, {
                title: getReceiptTitle(user),
                receiptLogoUrl: getReceiptLogoUrl(user),
                phone: order.tenant_phone || '',
                orderId: order.id,
                tableName: order.table_name,
                waiterName: order.waiter_name,
                subtotal,
                servicePercent,
                serviceAmount,
                discountAmount,
                depositAmount: toMoney(order.deposit_amount),
                appliedDeposit,
                grandTotal,
                total: grandTotal,
                note: order.note || null,
                receiptText: buildReceiptText(order, user),
                items: groupSetItems(order.items || []).map((item) => ({
                    name: item._isSetGroup ? `[SET] ${item.item_name}` : item.item_name,
                    qty: Number(item.qty),
                    qtyLabel: item._isSetGroup ? `${item.qty}ed` : formatQty(item.qty, item.sale_type),
                    unitPrice: Number(item.unit_price),
                    saleType: item._isSetGroup ? 'piece' : (item.sale_type || 'piece'),
                    note: item._isSetGroup ? null : (item.note || null),
                    total: parseFloat(item.unit_price) * Number(item.qty)
                }))
            });
            toast.success('Çek printerə göndərildi.');
        } catch (err) {
            toast.error(err.response?.data?.msg || err.message || 'Çap baş tutmadı.');
        } finally {
            setPrinting(false);
        }
    };

    if (loading) {
        return <div className="flex h-screen items-center justify-center text-gray-400">Yüklənir...</div>;
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <nav className="sticky top-0 z-50 flex h-14 items-center gap-3 border-b border-gray-200 bg-white px-4">
                <button
                    onClick={() => navigate('/waiter/tables')}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-500 transition-all hover:border-primary-400 hover:text-primary-500"
                >
                    ← Zala qayıt
                </button>
                <span className="font-bold text-gray-800">🧾 Qəbz #{orderId}</span>
                <button
                    onClick={handlePrint}
                    disabled={printing}
                    className="ml-auto rounded-lg bg-primary-500 px-3 py-1.5 text-xs font-bold text-white transition-all hover:bg-primary-600 disabled:opacity-50"
                >
                    {printing ? '⏳ Çap olunur...' : '🖨️ Çap et'}
                </button>
            </nav>

            <div className="mx-auto max-w-sm px-4 py-8">
                <div className="mb-6 text-center">
                    <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                        <span className="text-3xl">✅</span>
                    </div>
                    <h2 className="mb-1 text-xl font-bold text-gray-800">Ödəniş qəbul edildi</h2>
                    <p className="text-sm text-gray-400">Təşəkkür edirik!</p>
                </div>

                <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
                    <div className="border-b border-dashed border-gray-200 px-6 py-5 text-center">
                        <div className="mb-2 text-2xl">🧾</div>
                        <div className="text-lg font-bold text-gray-800">{getReceiptTitle(user)}</div>
                        <div className="mt-1 text-xs text-gray-400">Sifariş qəbzi</div>
                    </div>

                    <div className="border-b border-dashed border-gray-200 px-6 py-4">
                        <div className="grid grid-cols-2 gap-3">
                            {[
                                { label: 'Qəbz №', value: `#${orderId}` },
                                { label: 'Tarix', value: payment?.paid_at ? fmtDate(payment.paid_at) : fmtDate(new Date()) },
                                { label: 'Masa', value: order?.table_name },
                                { label: 'Ofisiant', value: order?.waiter_name }
                            ].map((meta) => (
                                <div key={meta.label}>
                                    <div className="mb-0.5 text-xs font-semibold uppercase tracking-wider text-gray-400">{meta.label}</div>
                                    <div className="text-sm font-semibold text-gray-700">{meta.value}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="border-b border-dashed border-gray-200 px-6 py-4">
                        <div className="mb-2 flex gap-2 border-b border-gray-100 pb-2 text-xs font-semibold uppercase text-gray-400">
                            <span className="flex-1">Məhsul</span>
                            <span className="w-12 text-center">Miq</span>
                            <span className="w-16 text-right">Cəmi</span>
                        </div>
                        {displayItems.map((item) => (
                            <div key={item.id} className="flex gap-2 border-b border-gray-50 py-1.5 last:border-0">
                                <div className="flex-1">
                                    <div className="text-sm text-gray-800">
                                        {item._isSetGroup ? `🎁 ${item.item_name}` : item.item_name}
                                    </div>
                                    {!item._isSetGroup && item.note && <div className="text-xs text-amber-500">📝 {item.note}</div>}
                                </div>
                                <span className="w-12 text-center font-mono text-xs text-gray-400">
                                    {item._isSetGroup ? `${item.qty}ed` : formatQty(item.qty, item.sale_type)}
                                </span>
                                <span className="w-16 text-right font-mono text-sm font-bold text-gray-700">
                                    {(parseFloat(item.unit_price) * Number(item.qty)).toFixed(2)}
                                </span>
                            </div>
                        ))}
                    </div>

                    <div className="border-b border-dashed border-gray-200 px-6 py-4">
                        <div className="mb-1 flex justify-between text-sm text-gray-500">
                            <span>Cəm</span>
                            <span className="font-mono">{formatMoney(subtotal)}</span>
                        </div>
                        <div className="mb-1 flex justify-between text-sm text-gray-500">
                            <span>{trimQty(servicePercent, 2)}% servis haqqı</span>
                            <span className="font-mono">{formatMoney(serviceAmount)}</span>
                        </div>
                        <div className="mb-1 flex justify-between text-sm text-gray-500">
                            <span>Depozit</span>
                            <span className="font-mono">{formatMoney(appliedDeposit)}</span>
                        </div>
                        <div className="mb-3 flex justify-between text-sm text-gray-500">
                            <span>Endirim</span>
                            <span className="font-mono">{formatMoney(discountAmount)}</span>
                        </div>
                        <div className="flex items-center justify-between border-t border-gray-200 pt-2">
                            <span className="font-bold text-gray-800">Ümumi cəm</span>
                            <span className="font-mono text-xl font-bold text-gray-800">{formatMoney(grandTotal)}</span>
                        </div>

                        {payment && (
                            <>
                                <div className="mt-2 flex justify-between text-sm text-gray-500">
                                    <span>Ödənildi</span>
                                    <span className="font-mono">{formatMoney(paid)}</span>
                                </div>
                                {payment.method === 'hesab' && (
                                    <>
                                        <div className="mt-1 flex justify-between text-sm text-gray-500">
                                            <span>Nağd</span>
                                            <span className="font-mono">{formatMoney(cashAmount)}</span>
                                        </div>
                                        <div className="mt-1 flex justify-between text-sm text-gray-500">
                                            <span>Kart</span>
                                            <span className="font-mono">{formatMoney(cardAmount)}</span>
                                        </div>
                                    </>
                                )}
                                {change > 0.005 && (
                                    <div className="mt-1 flex justify-between text-sm font-semibold text-green-600">
                                        <span>Qaytarıldı</span>
                                        <span className="font-mono">{formatMoney(change)}</span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="flex items-center justify-between border-b border-dashed border-gray-200 px-6 py-4">
                        <div className="flex items-center gap-2">
                            <span className="text-xl">{methodIcons[payment?.method] || '💵'}</span>
                            <span className="text-sm font-semibold text-gray-700">{methodNames[payment?.method] || 'Nağd'}</span>
                        </div>
                        <span className="text-xs text-gray-400">
                            {payment?.paid_at ? fmtTime(payment.paid_at) : ''}
                        </span>
                    </div>

                    <div className="px-6 py-5 text-center">
                        {order?.note && <div className="mb-3 text-xs text-gray-400">📝 {order.note}</div>}
                        <div className="text-xs text-gray-400">Fiscal POS v1.0.0 · fiscalbusiness.net</div>
                    </div>
                </div>

                <div className="mt-4 flex gap-3">
                    <button
                        onClick={() => navigate('/waiter/tables')}
                        className="flex-1 rounded-xl py-3 text-sm font-bold text-white transition-all hover:-translate-y-0.5"
                        style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}
                    >
                        ← Zala qayıt
                    </button>
                    <button
                        onClick={handlePrint}
                        disabled={printing}
                        className="rounded-xl border border-gray-200 px-4 py-3 text-sm font-bold text-gray-600 transition-all hover:bg-gray-50 disabled:opacity-50"
                    >
                        🖨️
                    </button>
                </div>
            </div>
        </div>
    );
};

export default WaiterReceipt;
